import { useCallback, useEffect, useMemo, useState } from 'react'
import type { CSSProperties } from 'react'
import { api, type Revenue } from '../api/client'
import { ConfirmDialog, UndoToast } from '../components/ConfirmDialog'
import { thisMonthJst } from '../utils/jst'

function fmt(n: number) { return n.toLocaleString('ja-JP') }
const MONTH_STORAGE_KEY = 'mangaos_month'
 
function getSavedMonth(): string {
  try {
    const saved = localStorage.getItem(MONTH_STORAGE_KEY)
    if (saved && /^\d{4}-\d{2}$/.test(saved)) return saved
  } catch {}
  return thisMonthJst()
}
type SortState = { key: string; dir: 'asc' | 'desc' }

function SortTh({ label, colKey, sort, onSort, style }: {
  label: string; colKey: string; sort: SortState
  onSort: (key: string) => void; style?: CSSProperties
}) {
  const active = sort.key === colKey
  return (
    <th onClick={() => onSort(colKey)}
      style={{ cursor: 'pointer', userSelect: 'none', whiteSpace: 'nowrap', ...style }}>
      {label}
      <span style={{ marginLeft: 3, opacity: active ? 1 : 0.2, fontSize: 10 }}>
        {active && sort.dir === 'desc' ? '▼' : '▲'}
      </span>
    </th>
  )
}

function RevTypeLabel({ type }: { type: string }) {
  const map: Record<string, [string, string]> = {
    manuscript_fee: ['原稿料',       'badge-blue'],
    royalty:        ['印税',         'badge-purple'],
    kaikai_kiki:    ['カイカイキキ', 'badge-amber'],
    advance:        ['先払い',       'badge-green'],
    other:          ['その他',       'badge-gray'],
  }
  const [label, cls] = map[type] ?? [type, 'badge-gray']
  return <span className={`badge ${cls}`} style={type === 'royalty' ? {
    background: 'var(--purple-light)', color: 'var(--purple)'
  } : {}}>{label}</span>
}

function AddRevenueModal({ onClose, onSaved }: { onClose: () => void; onSaved: (id: string, desc: string) => void }) {
  const [works, setWorks] = useState<any[]>([])
  const [form, setForm] = useState({
    revenue_type: 'manuscript_fee',
    work_id: '',
    amount: '',
    tax_reserve_rate: '33',
    expected_at: '',
    received_at: '',
    description: '',
  })
  const [saving, setSaving] = useState(false)
  const [errors, setErrors] = useState<Record<string, string>>({})
  const set = (k: string, v: string) => {
    setForm((f) => ({ ...f, [k]: v }))
    setErrors((e) => { const n = { ...e }; delete n[k]; return n })
  }

  useEffect(() => { api.works.list().then(setWorks) }, [])

  const reserve = Math.floor((Number(form.amount) || 0) * (Number(form.tax_reserve_rate) / 100))

  const handleSubmit = async () => {
    const e: Record<string, string> = {}
    if (!form.amount || Number(form.amount) <= 0) e.amount = '金額を入力してください'
    if (Object.keys(e).length > 0) { setErrors(e); return }
    setSaving(true)
    try {
      const result: any = await api.revenues.create({
        revenue_type: form.revenue_type,
        work_id: form.work_id || undefined,
        amount: Number(form.amount),
        tax_reserve_rate: Number(form.tax_reserve_rate) / 100,
        expected_at: form.expected_at || undefined,
        received_at: form.received_at || undefined,
        description: form.description || undefined,
      })
      const desc = form.description || `${form.revenue_type} ¥${Number(form.amount).toLocaleString()}`
      onSaved(result?.id ?? '', desc)
    } finally {
      setSaving(false)
    }
  }

  return (
    // ★ 入力途中の内容が消えないよう、背景クリックでは閉じない。
    //   閉じられるのは ✕ ボタン・キャンセルボタン・保存成功時のみ。
    <div className="overlay">
      <div className="modal">
        <div className="modal-header">
          <div className="modal-title">収入を追加</div>
          <button className="btn btn-ghost btn-sm" onClick={onClose}>✕</button>
        </div>
        <div className="modal-body">
          <div className="grid-2">
            <div className="form-group">
              <label>収入種別</label>
              <select value={form.revenue_type} onChange={(e) => set('revenue_type', e.target.value)}>
                <option value="manuscript_fee">原稿料</option>
                <option value="royalty">印税</option>
                <option value="kaikai_kiki">カイカイキキ</option>
                <option value="advance">先払い</option>
                <option value="other">その他</option>
              </select>
            </div>
            <div className="form-group">
              <label>作品（任意）</label>
              <select value={form.work_id} onChange={(e) => set('work_id', e.target.value)}>
                <option value="">—</option>
                {works.map((w: any) => <option key={w.id} value={w.id}>{w.title}</option>)}
              </select>
            </div>
          </div>
          <div className="grid-2">
            <div className="form-group">
              <label>金額（円）<span style={{ color: 'var(--red)' }}>*</span></label>
              <input type="number" placeholder="264000" value={form.amount}
                onChange={(e) => set('amount', e.target.value)}
                style={{ borderColor: errors.amount ? 'var(--red)' : undefined }} />
              {errors.amount && <div style={{ color: 'var(--red)', fontSize: 11, marginTop: 3 }}>{errors.amount}</div>}
            </div>
            <div className="form-group">
              <label>税積立率（%）</label>
              <input type="number" step="1" min="0" max="60" value={form.tax_reserve_rate}
                onChange={(e) => set('tax_reserve_rate', e.target.value)} />
            </div>
          </div>

          {form.amount && (
            <div style={{
              background: 'var(--paper-2)', borderRadius: 6, padding: '12px 14px', marginBottom: 14
            }}>
              <div style={{ fontSize: 11, color: 'var(--ink-3)', marginBottom: 8 }}>資金配分プレビュー</div>
              <div style={{ display: 'flex', gap: 16 }}>
                <div>
                  <div style={{ fontSize: 10, color: 'var(--ink-3)' }}>税積立</div>
                  <div style={{ fontFamily: 'var(--font-mono)', fontWeight: 600, color: 'var(--red)' }}>
                    ¥{fmt(reserve)}
                  </div>
                </div>
                <div>
                  <div style={{ fontSize: 10, color: 'var(--ink-3)' }}>税引後</div>
                  <div style={{ fontFamily: 'var(--font-mono)', fontWeight: 600, color: 'var(--green)' }}>
                    ¥{fmt((Number(form.amount) || 0) - reserve)}
                  </div>
                </div>
              </div>
            </div>
          )}

          <div className="grid-2">
            <div className="form-group">
              <label>入金予定日</label>
              <input type="date" value={form.expected_at}
                onChange={(e) => set('expected_at', e.target.value)} />
            </div>
            <div className="form-group">
              <label>実際の入金日（入金済の場合）</label>
              <input type="date" value={form.received_at}
                onChange={(e) => set('received_at', e.target.value)} />
            </div>
          </div>
          <div className="form-group">
            <label>備考</label>
            <input type="text" placeholder="例：週刊A 101話 原稿料" value={form.description}
              onChange={(e) => set('description', e.target.value)} />
          </div>
        </div>
        <div className="modal-footer">
          <button className="btn btn-outline" onClick={onClose}>キャンセル</button>
          <button className="btn btn-primary" onClick={handleSubmit} disabled={saving}>
            {saving ? '保存中...' : '追加'}
          </button>
        </div>
      </div>
    </div>
  )
}

// ── 追加: 収入編集モーダル ──
function EditRevenueModal({ revenue, onClose, onSaved }: {
  revenue: Revenue
  onClose: () => void
  onSaved: () => void
}) {
//  const [works, setWorks] = useState<any[]>([])
  const [form, setForm] = useState({
    revenue_type:     revenue.revenue_type     ?? 'manuscript_fee',
    amount:           String(revenue.amount    ?? ''),
    tax_reserve_rate: String(Math.round((revenue.tax_reserve_rate ?? 0.33) * 100)),
    expected_at:      revenue.expected_at      ?? '',
    received_at:      revenue.received_at      ?? '',
    description:      revenue.description      ?? '',
  })
  const [saving, setSaving] = useState(false)
  const set = (k: string, v: string) => setForm((f) => ({ ...f, [k]: v }))

//  useEffect(() => { api.works.list().then(setWorks) }, [])

  const reserve = Math.floor((Number(form.amount) || 0) * (Number(form.tax_reserve_rate) / 100))

  const handleSave = async () => {
    if (Number(form.amount) <= 0) return
    setSaving(true)
    try {
      await api.revenues.update(revenue.id, {
        revenue_type:     form.revenue_type,
        amount:           Number(form.amount),
        tax_reserve_rate: Number(form.tax_reserve_rate) / 100,
        expected_at:      form.expected_at  || null,
        received_at:      form.received_at  || null,
        description:      form.description  || null,
      })
      onSaved()
    } finally { setSaving(false) }
  }

  return (
    <div className="overlay" onClick={(e) => e.target === e.currentTarget && onClose()}>
      <div className="modal">
        <div className="modal-header">
          <div className="modal-title">収入を編集</div>
          <button className="btn btn-ghost btn-sm" onClick={onClose}>✕</button>
        </div>
        <div className="modal-body">
          <div className="grid-2">
            <div className="form-group">
              <label>収入種別</label>
              <select value={form.revenue_type} onChange={(e) => set('revenue_type', e.target.value)}>
                <option value="manuscript_fee">原稿料</option>
                <option value="royalty">印税</option>
                <option value="kaikai_kiki">カイカイキキ</option>
                <option value="advance">先払い</option>
                <option value="other">その他</option>
              </select>
            </div>
            <div className="form-group">
              <label>金額</label>
              <input type="number" value={form.amount}
                onChange={(e) => set('amount', e.target.value)} placeholder="500000" min="0" />
            </div>
          </div>
          <div className="grid-2">
            <div className="form-group">
              <label>税積立率 (%)</label>
              <input type="number" value={form.tax_reserve_rate}
                onChange={(e) => set('tax_reserve_rate', e.target.value)}
                placeholder="33" min="0" max="100" />
              {Number(form.amount) > 0 && (
                <div style={{ fontSize: 11, color: 'var(--red)', marginTop: 4 }}>
                  積立額 ¥{reserve.toLocaleString('ja-JP')}
                </div>
              )}
            </div>
            <div className="form-group">
              <label>入金予定日</label>
              <input type="date" value={form.expected_at}
                onChange={(e) => set('expected_at', e.target.value)} />
            </div>
          </div>
          <div className="grid-2">
            <div className="form-group">
              <label>入金日（確定時）</label>
              <input type="date" value={form.received_at}
                onChange={(e) => set('received_at', e.target.value)} />
            </div>
            <div className="form-group">
              <label>備考</label>
              <input type="text" value={form.description}
                onChange={(e) => set('description', e.target.value)}
                placeholder="週刊A 101話 原稿料" />
            </div>
          </div>
        </div>
        <div className="modal-footer">
          <button className="btn btn-outline" onClick={onClose}>キャンセル</button>
          <button className="btn btn-primary" onClick={handleSave} disabled={saving}>
            {saving ? '保存中...' : '保存'}
          </button>
        </div>
      </div>
    </div>
  )
}

export default function Finance() {
const [month, setMonthState] = useState<string>(getSavedMonth)
const setMonth = (m: string) => {
  setMonthState(m)
  try { localStorage.setItem(MONTH_STORAGE_KEY, m) } catch {}
}
  const [revenues, setRevenues] = useState<Revenue[]>([])
  const [summary, setSummary]   = useState<any>(null)
  const [loading, setLoading]   = useState(true)
  const [showAdd, setShowAdd]   = useState(false)
  const [undoState, setUndoState] = useState<{ id: string; desc: string } | null>(null)
  // ★ 追加: 削除確認
  const [deleteConfirm, setDeleteConfirm] = useState<{
    id: string; desc: string; isReceived: boolean
  } | null>(null)
  // ★ 追加: 編集モーダル
  const [editRevenue, setEditRevenue] = useState<Revenue | null>(null)

  const load = async () => {
    setLoading(true)
    const [revs, sum] = await Promise.all([
      api.revenues.list({ month }),
      api.revenues.summary(month),
    ])
    setRevenues(revs)
    setSummary(sum)
    setLoading(false)
  }

  useEffect(() => { load() }, [month])

  const markReceived = async (id: string) => {
    await api.revenues.markReceived(id)
    await load()
  }

  // ★ 追加: 収入削除実行
  const doDeleteRevenue = async (id: string) => {
    await api.revenues.delete(id)
    await load()
  }

  const received    = revenues.filter((r) =>  r.received_at)
  const notReceived = revenues.filter((r) => !r.received_at)

  const [pendingSort,  setPendingSort]  = useState<SortState>({ key: 'expected_at', dir: 'asc' })
  const [receivedSort, setReceivedSort] = useState<SortState>({ key: 'received_at', dir: 'desc' })

  const togglePendingSort = useCallback((key: string) => {
    setPendingSort((prev) => prev.key === key
      ? { key, dir: prev.dir === 'asc' ? 'desc' : 'asc' }
      : { key, dir: 'asc' })
  }, [])
  const toggleReceivedSort = useCallback((key: string) => {
    setReceivedSort((prev) => prev.key === key
      ? { key, dir: prev.dir === 'asc' ? 'desc' : 'asc' }
      : { key, dir: 'asc' })
  }, [])

  const sortRevenues = (list: typeof revenues, { key, dir }: SortState) => {
    return [...list].sort((a: any, b: any) => {
      const av = a[key] ?? ''
      const bv = b[key] ?? ''
      if (key === 'amount' || key === 'tax_reserve_amount')
        return dir === 'asc' ? Number(av) - Number(bv) : Number(bv) - Number(av)
      if (typeof av === 'string' && typeof bv === 'string')
        return dir === 'asc' ? av.localeCompare(bv, 'ja') : bv.localeCompare(av, 'ja')
      return dir === 'asc' ? Number(av) - Number(bv) : Number(bv) - Number(av)
    })
  }

  const sortedPending  = useMemo(() => sortRevenues(notReceived, pendingSort),  [notReceived, pendingSort])
  const sortedReceived = useMemo(() => sortRevenues(received,    receivedSort), [received, receivedSort])

  return (
    <div>
      {/* ★ 追加: 削除確認ダイアログ */}
      {deleteConfirm && (
        <ConfirmDialog
          title="収入を削除しますか？"
          message={[
            `「${deleteConfirm.desc}」を削除します。`,
            deleteConfirm.isReceived ? '入金済として記録されている収入です。' : '',
            'この操作は取り消せません。',
          ].filter(Boolean).join('\n')}
          confirmLabel="削除する"
          onConfirm={async () => {
            await doDeleteRevenue(deleteConfirm.id)
            setDeleteConfirm(null)
          }}
          onClose={() => setDeleteConfirm(null)}
        />
      )}

      {/* コントロール */}
      <div className="flex items-center justify-between mb-4">
        <input type="month" value={month} onChange={(e) => setMonth(e.target.value)}
          style={{ width: 'auto' }} />
        <button className="btn btn-primary btn-sm" onClick={() => setShowAdd(true)}>
          + 収入追加
        </button>
      </div>

      {/* サマリー */}
      {summary && (
        <div className="grid-4" style={{ marginBottom: 20 }}>
          <div className="metric">
            <div className="metric-label">当月入金済</div>
            <div className="metric-value green">¥{fmt(summary.total_received ?? 0)}</div>
          </div>
          <div className="metric">
            <div className="metric-label">税積立額</div>
            <div className="metric-value red">¥{fmt(summary.total_tax_reserve ?? 0)}</div>
            <div className="metric-sub">先取り済</div>
          </div>
          <div className="metric">
            <div className="metric-label">税引後手取り</div>
            <div className="metric-value">¥{fmt(summary.total_after_tax ?? 0)}</div>
          </div>
          <div className="metric">
            <div className="metric-label">内訳：原稿料</div>
            <div className="metric-value">¥{fmt(summary.manuscript_fee ?? 0)}</div>
            <div className="metric-sub">
              印税 ¥{fmt(summary.royalty ?? 0)} / KK ¥{fmt(summary.kaikai_kiki ?? 0)}
            </div>
          </div>
        </div>
      )}

      {/* ── 月次収入分解バー（選択月の合計、1本のみ） ── */}
      {summary && summary.total_received > 0 && (() => {
        const total    = summary.total_received as number
        const taxAmt   = summary.total_tax_reserve as number
        const netAmt   = summary.total_after_tax as number
        const taxPct   = total > 0 ? ((taxAmt / total) * 100).toFixed(1) : '0'
        const netPct   = total > 0 ? ((netAmt / total) * 100).toFixed(1) : '0'

        // 種別内訳
        const byType: { label: string; amount: number; color: string }[] = [
          { label: '原稿料',       amount: summary.manuscript_fee ?? 0, color: 'var(--chart-manuscript)' },
          { label: '印税',         amount: summary.royalty        ?? 0, color: 'var(--purple)' },
          { label: 'カイカイキキ', amount: summary.kaikai_kiki    ?? 0, color: 'var(--chart-kk)' },
        ].filter((b) => b.amount > 0)
        const otherAmt = total
          - (summary.manuscript_fee ?? 0)
          - (summary.royalty        ?? 0)
          - (summary.kaikai_kiki    ?? 0)
        if (otherAmt > 0) byType.push({ label: 'その他', amount: otherAmt, color: 'var(--chart-other)' })

        return (
          <div style={{
            background: 'var(--chart-panel-bg)',
            border: '1px solid #e2dcd3',
            borderRadius: 10,
            padding: '16px 20px',
            marginBottom: 20,
          }}>
            <div style={{
              fontSize: 11, color: 'var(--ink-3)', fontWeight: 600,
              letterSpacing: '0.06em', marginBottom: 12,
            }}>
              {month} 入金済　月次分解
            </div>

            {/* 分解バー */}
            <div style={{
              display: 'flex', height: 26,
              borderRadius: 5, overflow: 'hidden', gap: 2,
            }}>
              {/* 税積立 */}
              <div style={{
                width: `${taxPct}%`,
                background: 'var(--chart-tax)',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                fontSize: 10, color: '#fff', fontWeight: 600,
                transition: 'width 0.4s ease',
              }}>
                {parseFloat(taxPct) >= 10 ? '税積立' : ''}
              </div>
              {/* 手取り */}
              <div style={{
                flex: 1,
                background: 'var(--chart-profit)',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                fontSize: 10, color: '#fff', fontWeight: 600,
                transition: 'width 0.4s ease',
              }}>
                {parseFloat(netPct) >= 10 ? '手取り' : ''}
              </div>
            </div>

            {/* 金額ラベル */}
            <div style={{
              display: 'flex', justifyContent: 'space-between',
              marginTop: 8, fontSize: 12,
            }}>
              <span style={{ color: 'var(--chart-tax)', fontWeight: 600 }}>
                税積立 ¥{fmt(taxAmt)}
                <span style={{ fontWeight: 400, color: 'var(--chart-tax)', marginLeft: 4 }}>
                  ({taxPct}%)
                </span>
              </span>
              <span style={{ color: 'var(--chart-profit)', fontWeight: 600 }}>
                手取り ¥{fmt(netAmt)}
                <span style={{ fontWeight: 400, color: 'var(--chart-profit)', marginLeft: 4 }}>
                  ({netPct}%)
                </span>
              </span>
              <span style={{ color: 'var(--ink)', fontWeight: 700 }}>
                合計 ¥{fmt(total)}
              </span>
            </div>

            {/* 種別内訳チップ */}
            {byType.length > 0 && (
              <div style={{
                display: 'flex', gap: 8, flexWrap: 'wrap',
                marginTop: 14, paddingTop: 12,
                borderTop: '1px solid #e2dcd3',
              }}>
                {byType.map((b) => (
                  <div key={b.label} style={{
                    display: 'flex', alignItems: 'center', gap: 6,
                    background: 'var(--chart-chip-bg)', border: '1px solid #e2dcd3',
                    borderRadius: 6, padding: '4px 10px', fontSize: 12,
                  }}>
                    <span style={{
                      width: 8, height: 8, borderRadius: 2,
                      background: b.color, display: 'inline-block', flexShrink: 0,
                    }} />
                    <span style={{ color: 'var(--ink-3)' }}>{b.label}</span>
                    <span style={{ fontFamily: 'var(--font-mono)', fontWeight: 600, color: 'var(--ink)' }}>
                      ¥{fmt(b.amount)}
                    </span>
                  </div>
                ))}
              </div>
            )}
          </div>
        )
      })()}

      {loading ? <div className="loading">LOADING...</div> : (
        <>
          {/* 入金待ち */}
          {notReceived.length > 0 && (
            <div className="card" style={{ marginBottom: 16 }}>
              <div className="card-header">
                <span className="card-title">入金待ち</span>
                <span className="badge badge-amber">{notReceived.length}件</span>
              </div>
              <div className="table-wrap">
                <table>
                  <thead>
                    <tr>
                      <SortTh label="種別"     colKey="revenue_type"      sort={pendingSort} onSort={togglePendingSort} />
                      <SortTh label="内容"     colKey="description"       sort={pendingSort} onSort={togglePendingSort} />
                      <SortTh label="金額"     colKey="amount"            sort={pendingSort} onSort={togglePendingSort} />
                      <SortTh label="税積立"   colKey="tax_reserve_amount" sort={pendingSort} onSort={togglePendingSort} />
                      <SortTh label="入金予定" colKey="expected_at"       sort={pendingSort} onSort={togglePendingSort} />
                      <th></th>
                    </tr>
                  </thead>
                  <tbody>
                    {sortedPending.map((r) => (
                      <tr key={r.id}>
                        <td><RevTypeLabel type={r.revenue_type} /></td>
                        <td style={{ fontSize: 12 }}>{r.description ?? r.work_title ?? '—'}</td>
                        <td className="mono">¥{fmt(r.amount)}</td>
                        <td className="mono" style={{ color: 'var(--red)', fontSize: 12 }}>
                          ¥{fmt(r.tax_reserve_amount ?? 0)}
                        </td>
                        <td style={{ fontSize: 12 }}>{r.expected_at ?? '—'}</td>
                        <td>
                          <div style={{ display: 'flex', gap: 4 }}>
                            <button className="btn btn-sm btn-primary" onClick={() => markReceived(r.id)}>
                              入金確認
                            </button>
                            <button className="btn btn-sm btn-outline"
                              onClick={() => setEditRevenue(r)} title="金額・内容を編集">
                              編集
                            </button>
                            <button className="btn btn-sm btn-ghost" style={{ color: 'var(--red)' }}
                              onClick={() => setDeleteConfirm({
                                id: r.id,
                                desc: r.description ?? r.work_title ?? `¥${fmt(r.amount)}`,
                                isReceived: false,
                              })}
                              title="この収入を削除">
                              削除
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* 入金済 */}
          <div className="card">
            <div className="card-header">
              <span className="card-title">入金済</span>
              <span className="badge badge-green">{received.length}件</span>
            </div>
            <div className="table-wrap">
              <table>
                <thead>
                  <tr>
                    <SortTh label="種別"   colKey="revenue_type"       sort={receivedSort} onSort={toggleReceivedSort} />
                    <SortTh label="内容"   colKey="description"        sort={receivedSort} onSort={toggleReceivedSort} />
                    <SortTh label="金額"   colKey="amount"             sort={receivedSort} onSort={toggleReceivedSort} />
                    <SortTh label="税積立" colKey="tax_reserve_amount" sort={receivedSort} onSort={toggleReceivedSort} />
                    <SortTh label="入金日" colKey="received_at"        sort={receivedSort} onSort={toggleReceivedSort} />
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {received.length === 0 && (
                    <tr>
                      <td colSpan={6} className="text-muted" style={{ textAlign: 'center', padding: 24 }}>
                        入金なし
                      </td>
                    </tr>
                  )}
                  {sortedReceived.map((r) => (
                    <tr key={r.id}>
                      <td><RevTypeLabel type={r.revenue_type} /></td>
                      <td style={{ fontSize: 12 }}>{r.description ?? r.work_title ?? '—'}</td>
                      <td className="mono">¥{fmt(r.amount)}</td>
                      <td className="mono" style={{ color: 'var(--red)', fontSize: 12 }}>
                        ¥{fmt(r.tax_reserve_amount ?? 0)}
                      </td>
                      <td className="mono" style={{ fontSize: 12 }}>{r.received_at}</td>
                      <td>
                        <div style={{ display: 'flex', gap: 4 }}>
                          <button className="btn btn-sm btn-outline"
                            onClick={() => setEditRevenue(r)} title="金額・内容を編集">
                            編集
                          </button>
                          <button className="btn btn-sm btn-ghost" style={{ color: 'var(--red)' }}
                            onClick={() => setDeleteConfirm({
                              id: r.id,
                              desc: r.description ?? r.work_title ?? `¥${fmt(r.amount)}`,
                              isReceived: true,
                            })}
                            title="この収入を削除">
                            削除
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}

      {showAdd && (
        <AddRevenueModal
          onClose={() => setShowAdd(false)}
          onSaved={(id, desc) => {
            setShowAdd(false)
            load()
            if (id) setUndoState({ id, desc })
          }}
        />
      )}
      {editRevenue && (
        <EditRevenueModal
          revenue={editRevenue}
          onClose={() => setEditRevenue(null)}
          onSaved={() => { setEditRevenue(null); load() }}
        />
      )}
      {undoState && (
        <UndoToast
          msg={`「${undoState.desc}」を追加しました`}
          onUndo={async () => { await api.revenues.delete(undoState.id); await load() }}
          onClose={() => setUndoState(null)}
        />
      )}
    </div>
  )
}